import type { ReactNode } from 'react';
import type { UserRole } from 'api/vektra/types';

// Ported verbatim from vektra-client-deprecated/packages/ui/src/RoleGate.tsx.

interface RoleGateProps {
  children: ReactNode;
  userRole: UserRole | undefined;
  allowed: UserRole[];
  fallback?: ReactNode;
}

export function RoleGate({ children, userRole, allowed, fallback = null }: RoleGateProps) {
  if (userRole && allowed.includes(userRole)) {
    return <>{children}</>;
  }
  return <>{fallback}</>;
}

const MANAGEMENT_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNT_MANAGER', 'SALES_MANAGER', 'FLEET_MANAGER'];

const ADMIN_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNT_MANAGER'];

const FINANCIAL_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNT_MANAGER', 'SALES_MANAGER', 'FINANCIAL_MANAGER'];

export function canManageFleet(role: UserRole | undefined): boolean {
  return !!role && [...ADMIN_ROLES, 'FLEET_MANAGER'].includes(role);
}

// WH-01 (VEK-203) — the Warehouses list's controls transcribe WarehouseController's role sets:
// create / update / set-primary admit the fleet manager, delete does not.
export function canManageWarehouses(role: UserRole | undefined): boolean {
  return !!role && [...ADMIN_ROLES, 'FLEET_MANAGER'].includes(role);
}

export function canDeleteWarehouse(role: UserRole | undefined): boolean {
  return !!role && ADMIN_ROLES.includes(role);
}

// MAINT-03 — mirrors the @PreAuthorize on PUT /maintenance/{id}/checklist: the fleet marks a service's checklist,
// operators included; scheduling and starting stay with canManageFleet.
export function canCheckMaintenance(role: UserRole | undefined): boolean {
  return !!role && [...ADMIN_ROLES, 'FLEET_MANAGER', 'FLEET_OPERATOR'].includes(role);
}

// VEK-65 — mirrors the @PreAuthorize on POST /vehicles/{id}/fuel/refills. Operators refuel in the
// field; sales and financial managers do not.
export function canRefuelVehicle(role: UserRole | undefined): boolean {
  return !!role && [...ADMIN_ROLES, 'FLEET_MANAGER', 'FLEET_OPERATOR'].includes(role);
}

// VPARAM-01 — mirrors the @PreAuthorize on /vehicle-parameters (the vehicle create/update role set).
// Read by primary role: the Parameters button is for the account owner and the fleet manager.
export function canManageVehicleParameters(role: UserRole | undefined): boolean {
  return role === 'ACCOUNT_MANAGER' || role === 'FLEET_MANAGER';
}

export function canManageWorkOrders(role: UserRole | undefined): boolean {
  return !!role && MANAGEMENT_ROLES.includes(role);
}

export function canManageCustomers(role: UserRole | undefined): boolean {
  return !!role && [...ADMIN_ROLES, 'SALES_MANAGER'].includes(role);
}

export function canConfirmPayments(role: UserRole | undefined): boolean {
  return !!role && FINANCIAL_ROLES.includes(role);
}

export function canAssignOperators(role: UserRole | undefined): boolean {
  return !!role && [...ADMIN_ROLES, 'FLEET_MANAGER'].includes(role);
}

export function canManageUsers(role: UserRole | undefined): boolean {
  return !!role && ADMIN_ROLES.includes(role);
}

// Administration sidebar group (users, billing/maintenance/inspection config,
// operational schedule, sensors, notifications) is restricted to admin roles.
export function canAccessAdministration(role: UserRole | undefined): boolean {
  return !!role && ADMIN_ROLES.includes(role);
}

export function isOperator(role: UserRole | undefined): boolean {
  return role === 'FLEET_OPERATOR';
}

// MENU-02 (VEK-47) — the header's What's new tile is the account owner's alone. Deliberately
// narrower than ACCOUNT_OWNER_COLUMN below: SUPER_ADMIN, ADMIN and MY_ACCOUNTS do not see it.
// Read by primary role, never the expanded `roles` claim (an ADMIN's claim carries ACCOUNT_MANAGER).
export function canSeeWhatsNew(role: UserRole | undefined): boolean {
  return role === 'ACCOUNT_MANAGER';
}

// ── MENU-01 — the sidebar matrix ──────────────────────────────────────────────────────────────
//
// One table, keyed by the menu item ids in `menu-items/vektra.ts`, read by primary role. It is
// deliberately NOT derived from the JWT's inheritance-expanded `roles` claim: that claim expands
// downward, so an ACCOUNT_MANAGER holds FLEET_OPERATOR and would inherit My Tasks — which the
// owner's matrix withholds from the account owner. "Which seat is this" is an identity question,
// the carve-out ROLE-01 records; see openspec/specs/navigation/spec.md.
//
// SUPER_ADMIN, ADMIN and MY_ACCOUNTS are absent from the owner's matrix and take the
// ACCOUNT_MANAGER column (gate decision D5, 2026-09-14). `scanner` and `notifications` are not
// rows here — they were deleted from `menu-items/vektra.ts` (their routes stay).
export type MenuEntryId =
  | 'dashboard'
  | 'work-orders'
  | 'work-orders-tasks'
  | 'fleet'
  | 'calendar'
  | 'map'
  | 'customers'
  | 'maintenance'
  | 'work-logs'
  | 'pay-rates'
  | 'pay-rates-admin'
  | 'analytics'
  | 'admin'
  | 'warehouses'
  | 'profile'
  | 'support';

const ACCOUNT_OWNER_COLUMN: UserRole[] = ['ACCOUNT_MANAGER', 'SUPER_ADMIN', 'ADMIN', 'MY_ACCOUNTS'];

export const MENU_ACCESS: Record<MenuEntryId, UserRole[]> = {
  // NAV-01 (VEK-93) — `dashboard` is the Home entry (/home) since the reporting page moved to /analytics; the
  // id is historical. `analytics` takes the same column: a relocation changes nobody's access.
  // HOME-01 (VEK-95) — the sales manager's /home is the day agenda, so Home is drawn for that seat too; the three
  // seats whose /home is their My Tasks queue keep reaching it through My Tasks and the sign-in landing.
  dashboard: [...ACCOUNT_OWNER_COLUMN, 'SALES_MANAGER'],
  'work-orders': [...ACCOUNT_OWNER_COLUMN, 'SALES_MANAGER', 'FLEET_MANAGER', 'FINANCIAL_MANAGER'],
  'work-orders-tasks': ['FLEET_MANAGER', 'FLEET_OPERATOR', 'FINANCIAL_MANAGER'],
  fleet: [...ACCOUNT_OWNER_COLUMN, 'FLEET_MANAGER', 'FINANCIAL_MANAGER'],
  calendar: [...ACCOUNT_OWNER_COLUMN, 'SALES_MANAGER', 'FLEET_MANAGER'],
  map: [...ACCOUNT_OWNER_COLUMN],
  customers: [...ACCOUNT_OWNER_COLUMN],
  // WORKLOG-12 (VEK-176, gate D5b) — the operator's surface is My Tasks and Work hours: Maintenance is hidden by menu
  // only; `/maintenance` and `GET /maintenance` still admit the role by URL.
  maintenance: [...ACCOUNT_OWNER_COLUMN, 'FLEET_MANAGER'],
  // WORKLOG-03 (VEK-145, gate G5) — the reviewing roles `GET /work-logs` admits; MY_ACCOUNTS is left out because the
  // server refuses it. WORKLOG-11 (VEK-170) — plus FLEET_OPERATOR, whose /work-logs is their own page on
  // `GET /work-logs/mine`: the row is the union by seat of the two endpoints' role sets.
  'work-logs': ['ACCOUNT_MANAGER', 'SUPER_ADMIN', 'ADMIN', 'FLEET_MANAGER', 'FLEET_OPERATOR'],
  // COMM-02 (VEK-147, gate C5) — the fleet manager reads Pay rates, does not save. MENU-03 (VEK-179, gate 2026-10-21) —
  // the page is drawn in two placements by seat: this workspace row is the fleet manager's alone, and the owner column
  // takes `pay-rates-admin` in the Administration group. Each row is narrower than `GET /pay-rates`' role set; their
  // union is exactly that set. Before MENU-03 this one row was the owner column plus FLEET_MANAGER.
  'pay-rates': ['FLEET_MANAGER'],
  // MENU-03 (VEK-179) — the Administration placement. MY_ACCOUNTS is left out although `admin` admits it, because
  // `GET /pay-rates` refuses that role (the same exception MENU-01 records for Work hours and Pay rates).
  'pay-rates-admin': ['ACCOUNT_MANAGER', 'SUPER_ADMIN', 'ADMIN'],
  analytics: [...ACCOUNT_OWNER_COLUMN],
  admin: [...ACCOUNT_OWNER_COLUMN],
  // WH-01 (VEK-203) — the Administration group's Warehouses entry (it replaced `add-warehouse`, which had no row
  // of its own). MY_ACCOUNTS is left out although `admin` admits it, because `GET /v1/warehouses` refuses that role —
  // the same exception `pay-rates-admin` records.
  warehouses: ['ACCOUNT_MANAGER', 'SUPER_ADMIN', 'ADMIN'],
  profile: [...ACCOUNT_OWNER_COLUMN, 'SALES_MANAGER', 'FLEET_MANAGER', 'FLEET_OPERATOR', 'FINANCIAL_MANAGER'],
  // SUPPORT-01 (VEK-161) — every seat, exactly the roles `GET /support-requests` admits.
  support: [...ACCOUNT_OWNER_COLUMN, 'SALES_MANAGER', 'FLEET_MANAGER', 'FLEET_OPERATOR', 'FINANCIAL_MANAGER']
};

// An id the matrix does not know is not "hidden" — it is a programming error on the menu side,
// surfaced as visible so a new entry is noticed rather than silently dropped.
export function canSeeMenuEntry(role: UserRole | undefined, id: string | undefined): boolean {
  if (!id || !(id in MENU_ACCESS)) return true;
  return !!role && MENU_ACCESS[id as MenuEntryId].includes(role);
}
